// Portuguese copy for /face-data-policy (hand-written from copy.en.ts; same shape, same facts). Brazilian Portuguese,
// like /face-data-changes-2026. The detailed documents it links to are in English.
import type { FaceDataPolicyCopy } from "./copy.en";

const CONTACT =
  "CLOUDCONCUR LTD (empresa n.º 13399913), operando como Photohawk, 23 Caldey Gardens, Ingleby Barwick, Stockton-on-Tees, TS17 5HW, Reino Unido. E-mail: support@photohawk.com.";

const pt: FaceDataPolicyCopy = {
  seo: {
    title: "Política de dados faciais | Photohawk",
    description:
      "Quais dados faciais a Photohawk cria, onde são tratados, para que são usados e quando são excluídos: uma política para o Photohawk Studio (galerias de eventos e empresas) e outra para o app Photohawk (o app para as fotos da sua gente).",
  },
  title: "Política de dados faciais",
  version: "1.3",
  effectiveDate: "31 de outubro de 2026",
  meta: "Versão {version} · Em vigor desde {date}",
  intro: [
    "A Photohawk tem dois produtos que encontram as pessoas nas fotos, e cada um tem sua própria política de dados faciais abaixo. O Photohawk Studio administra galerias de eventos e empresas, onde cada pessoa encontra as suas fotos com uma selfie. O app Photohawk é para as fotos da sua gente, no seu próprio celular.",
    "Os dois são mantidos separados. Os dados faciais das galerias do Photohawk Studio e os do app Photohawk nunca são compartilhados, misturados ou comparados entre si. Nenhum deles é vendido ou usado para publicidade, e dados faciais nunca são uma foto: excluí-los nunca exclui uma foto.",
  ],
  tabsLabel: "Escolha um produto",
  studio: {
    id: "studio",
    tab: "Photohawk Studio",
    heading: "Photohawk Studio — galerias de eventos e empresas",
    summary:
      "A busca por selfie é opcional e é ativada pelo administrador de cada galeria. Ela permite que uma pessoa encontre as suas próprias fotos naquela galeria com uma selfie.",
    sections: [
      {
        heading: "1. Quais dados faciais são criados",
        blocks: [
          {
            type: "ul",
            items: [
              "Modelos faciais: representações numéricas dos rostos detectados nas fotos de uma galeria, guardadas no índice de busca facial daquela galeria (Amazon Rekognition) ou, nas galerias que usam um dos nossos próprios modelos faciais, como vetores faciais.",
              "O arquivo de rostos: pequenos recortes de rostos guardados de forma privada para que a busca facial de uma galeria possa ser reconstruída sem ler de novo as fotos originais. Nunca é mostrado a ninguém nem publicado.",
              "As selfies usadas para buscar não são dados faciais que guardamos: uma selfie é comparada com os modelos faciais daquela galeria e depois descartada. Ela não é guardada e nenhum modelo facial é criado a partir dela.",
            ],
          },
          {
            type: "p",
            text: "Encontrar pessoas nas fotos (corpos inteiros, sem identificar ninguém) e ler números de peito e textos não usa dados faciais. A busca por número de peito não é afetada por esta política.",
          },
        ],
      },
      {
        heading: "2. Onde são tratados",
        blocks: [
          {
            type: "ul",
            items: [
              "Na nuvem da Photohawk, na Amazon Web Services na União Europeia (Irlanda, eu-west-1), depois que uma foto é enviada.",
              "Cada galeria tem o seu próprio índice de busca facial (uma coleção do Amazon Rekognition). Uma selfie só é buscada na galeria para a qual foi tirada.",
              "Para ganhar tempo, os apps de envio do Photohawk Studio podem encontrar os rostos no computador ou no celular que envia as fotos e mandar pequenos recortes de rostos junto com a foto. Os modelos faciais são sempre criados na nossa nuvem.",
            ],
          },
        ],
      },
      {
        heading: "3. Para que são usados e para que nunca são usados",
        blocks: [
          {
            type: "p",
            text: "Os dados faciais são usados apenas para que as pessoas encontrem as suas próprias fotos naquela galeria com uma selfie.",
          },
          {
            type: "p",
            text: "Eles nunca são vendidos, nunca são usados para publicidade, nunca são usados para identificar alguém fora da galeria e nunca são misturados ou comparados com os dados faciais do app Photohawk.",
          },
        ],
      },
      {
        heading: "4. Por quanto tempo são guardados e quando são excluídos",
        blocks: [
          {
            type: "p",
            text: "A partir de 1 de novembro de 2026, os dados faciais são excluídos de forma permanente no primeiro destes momentos:",
          },
          {
            type: "ul",
            items: [
              "o prazo que o administrador da galeria escolheu: 30, 90 ou 180 dias ou 12 meses depois do envio da foto (foto a foto, mesmo que a galeria continue online). O padrão é «Sem prazo»: nada é excluído pela idade;",
              "quando a galeria ou a foto é excluída;",
              "30 dias depois que a busca facial da galeria é desativada (cancelado se o administrador reativá-la nesses 30 dias);",
              "em até 24 horas depois que o espaço de trabalho do administrador é fechado;",
              "30 dias depois que uma pessoa nos pede para excluir os seus dados faciais ou retira o seu consentimento;",
              "45 dias depois que a nossa revisão anual concluir que eles não são mais necessários.",
            ],
          },
          {
            type: "p",
            text: "Arquivar uma galeria não exclui os seus dados faciais: uma galeria arquivada continua online e mantém a busca facial.",
          },
          {
            type: "p",
            text: "Até 31 de outubro de 2026, os dados faciais são excluídos quando uma galeria expira (em cerca de 60 dias) e quando uma galeria é excluída. Os modelos faciais não têm backup, então uma exclusão é definitiva; os backups do nosso banco de dados guardam apenas referências a rostos (nunca modelos) e são apagados em até 35 dias. Fotos, vendas, downloads e a busca por número de peito não são afetados.",
          },
          {
            type: "link",
            text: "O calendário de conservação completo e como funciona a exclusão (em inglês):",
            label: "Biometric data retention policy",
            href: "/biometric-data-retention",
          },
        ],
      },
      {
        heading: "5. Base legal",
        blocks: [
          {
            type: "p",
            text: "Fotos. As fotos de uma galeria são compartilhadas para que os participantes possam encontrar e comprar as suas próprias fotos. Elas são tratadas porque isso é necessário para a finalidade da galeria, e guardadas apenas pelo tempo proporcional a essa finalidade (veja o §4).",
          },
          {
            type: "p",
            text: "Busca facial. Dados faciais são dados biométricos e recebem proteção extra:",
          },
          {
            type: "ul",
            items: [
              "Quando são usados: apenas para que as pessoas possam encontrar as suas próprias fotos naquela galeria. Nunca são usados para identificar ninguém para qualquer outra finalidade.",
              "Por quanto tempo são guardados: pelo tempo que o administrador escolher (§4). São excluídos quando esse prazo termina, quando a busca facial é desativada ou a galeria é excluída, ou antes, se alguém pedir.",
              "A pessoa que busca é convidada a dar o seu consentimento explícito antes de a selfie dela ser usada. A selfie nunca é guardada.",
              "O administrador decide se a busca facial é adequada para o seu evento e se é permitida no local onde ele acontece. Quando a lei local exige o consentimento das pessoas que aparecem nas fotos, o administrador precisa tê-lo, por exemplo por meio da inscrição no evento. Se não o tiver, deve usar a busca por número de peito e por horário, que não usa dados faciais.",
              "Qualquer pessoa pode pedir a qualquer momento que os seus dados faciais sejam excluídos, e eles são excluídos em até 30 dias (§8).",
            ],
          },
          {
            type: "p",
            text: "Para as pessoas de uma galeria, o administrador da galeria é o controlador; a Photohawk trata os dados faciais em nome dele, com o nosso acordo de tratamento de dados.",
          },
        ],
      },
      {
        heading: "6. Illinois e outros estados dos EUA",
        blocks: [
          {
            type: "p",
            text: "Esta política e a política de conservação de dados biométricos são o nosso calendário de conservação público e por escrito. O administrador escolhe por quanto tempo os dados faciais são guardados (§4). Administradores cujos eventos estejam sujeitos à lei de Illinois sobre informações biométricas (BIPA: quando a finalidade é cumprida ou em até 3 anos desde a última interação da pessoa), à CUBI do Texas, à Lei de Privacidade do Colorado ou à RCW 19.375 de Washington devem escolher um prazo dentro desses limites. Nenhuma lei estabelece um prazo único.",
          },
        ],
      },
      {
        heading: "7. Quem os trata",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition e armazenamento), região UE (Irlanda), como nosso suboperador, apenas segundo as nossas instruções. Não revelamos dados faciais a mais ninguém, a menos que a lei exija.",
          },
        ],
      },
      {
        heading: "8. Pedidos e contato",
        blocks: [
          {
            type: "ul",
            items: [
              "Na galeria: abra a busca por selfie e escolha “Make a request”. O pedido vai para o administrador da galeria e para a Photohawk.",
              "Por e-mail: support@photohawk.com, dizendo a qual galeria ou evento se refere. Atendemos o pedido em até 30 dias.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "O calendário de conservação detalhado do Studio (em inglês):",
      label: "Biometric data retention policy",
      href: "/biometric-data-retention",
    },
  },
  family: {
    id: "family",
    tab: "App Photohawk",
    heading: "O app Photohawk — para as fotos da sua gente",
    summary:
      "Agrupar as pessoas das suas fotos é o que o app (nas lojas de apps como “Photohawk”) faz, por isso ele pede o seu consentimento explícito antes de criar dados faciais: primeiro para agrupar rostos e depois para compará-los na nuvem. Os rostos são encontrados no seu celular; para agrupá-los, só pequenos recortes de rostos (nunca suas fotos) vão para a sua própria coleção privada de rostos na nossa nuvem. Você pode excluir tudo quando quiser.",
    sections: [
      {
        heading: "1. Quais dados faciais são criados",
        blocks: [
          {
            type: "ul",
            items: [
              "No seu celular: onde estão os rostos em cada foto ou vídeo, quais rostos são da mesma pessoa, os nomes e correções que você adiciona, aniversários e parentescos, e a faixa de idade estimada de cada rosto (devolvida pela nuvem, que não a guarda). Tudo isso fica só no seu celular.",
              "Na nuvem: pequenos recortes de rostos (nunca fotos ou vídeos inteiros) e os modelos faciais (uma lista de números por rosto) e os grupos que o nosso serviço na nuvem cria a partir deles.",
            ],
          },
          {
            type: "p",
            text: "Reconhecer pets e o tipo de cena das suas fotos não usa dados faciais.",
          },
        ],
      },
      {
        heading: "2. Onde são tratados",
        blocks: [
          {
            type: "ul",
            items: [
              "No seu celular: os rostos são encontrados no celular, e os dados faciais ficam no armazenamento privado do app, fora dos backups.",
              "Na nuvem: os recortes de rostos vão para a sua própria coleção de rostos no Amazon Rekognition (Amazon Web Services, região UE (Irlanda)), que guarda só os seus rostos. Os recortes são excluídos assim que são comparados (o que sobrar é excluído depois de um dia).",
              "Nunca recebemos suas fotos ou vídeos para isso (exceto as fotos que você compartilha, criptografadas para que não possamos vê-las, e as impressões que você pede), nem os nomes, aniversários ou parentescos que você adiciona.",
            ],
          },
        ],
      },
      {
        heading: "3. Para que são usados, e para que nunca",
        blocks: [
          {
            type: "p",
            text: "Os dados faciais são usados só para agrupar suas fotos e vídeos pelas pessoas que aparecem, para buscar por pessoa, para ajudar você a escolher quais fotos compartilhar e para que quem parece uma criança nunca seja oferecido como destinatário de fotos.",
          },
          {
            type: "p",
            text: "Nunca são vendidos, nunca usados para publicidade, perfis ou verificação de identidade, nunca para identificar alguém fora das suas próprias fotos, nunca comparados entre usuários, nunca usados para treinar modelos e nunca misturados nem comparados com os dados faciais do Photohawk Studio. Nunca fazem parte do que você compartilha.",
          },
        ],
      },
      {
        heading: "4. Por quanto tempo são guardados e quando são excluídos",
        blocks: [
          {
            type: "p",
            text: "No seu celular, os dados faciais ficam guardados até você tocar em “Excluir todos os dados faciais”, excluir a sua conta, excluir o app ou passar 3 anos sem usar o Photohawk. Os dados faciais de uma foto vão embora quando a foto sai do seu celular.",
          },
          {
            type: "p",
            text: "Na nuvem, os dados faciais são excluídos quando você toca em “Excluir todos os dados faciais”, quando exclui a sua conta do Photohawk, quando nos pede (em até 30 dias) e depois de 3 anos sem usar o Photohawk. Não são excluídos quando um plano ou teste grátis termina.",
          },
          {
            type: "p",
            text: "A exclusão na nuvem apaga toda a sua coleção de rostos. Excluir dados faciais nunca exclui fotos ou vídeos.",
          },
        ],
      },
      {
        heading: "5. Base legal e consentimento",
        blocks: [
          {
            type: "p",
            text: "Nós nos baseamos no seu consentimento explícito (art. 9(2)(a) do RGPD, art. 11 da LGPD e o equivalente em outras leis), pedido duas vezes e separadamente antes de o app analisar suas fotos: para agrupar rostos (a caixa começa desmarcada) e para compará-los na nuvem. Os dois são necessários para usar o app; se você não concordar, nenhum dado facial é criado e você pode excluir a sua conta. Você pode retirá-lo a qualquer momento tocando em “Excluir todos os dados faciais”: tudo é excluído, e o app pergunta de novo antes de criar mais. Se o texto mudar, o app pergunta de novo.",
          },
        ],
      },
      {
        heading: "6. Illinois",
        blocks: [
          {
            type: "p",
            text: "O app Photohawk não é oferecido a quem informa que mora em Illinois (o app pergunta; não usa GPS nem o seu endereço IP). O nosso serviço também recusa a comparação na nuvem para Illinois, e se você mudar o lugar onde mora para Illinois, os dados faciais existentes são excluídos no celular e na nuvem.",
          },
        ],
      },
      {
        heading: "7. Quem trata os dados",
        blocks: [
          {
            type: "p",
            text: "Amazon Web Services (Amazon Rekognition), região UE (Irlanda), como operador, só seguindo as nossas instruções. Os dados faciais que ficam no seu celular nunca são enviados a ninguém.",
          },
        ],
      },
      {
        heading: "8. Pedidos e contato",
        blocks: [
          {
            type: "ul",
            items: [
              "No app: Compartilhamento e privacidade → “Excluir todos os dados faciais”, ou Excluir conta.",
              "Por e-mail: support@photohawk.com, para acessar ou excluir seus dados faciais na nuvem.",
              CONTACT,
            ],
          },
        ],
      },
    ],
    full: {
      text: "A política de dados faciais completa do app Photohawk, com o seu calendário de conservação (em inglês):",
      label: "Photohawk app: Face Data Policy",
      href: "/face-data-policy/family",
    },
  },
  changelog: {
    title: "Alterações",
    items: [
      "1.3, 31 de outubro de 2026: Photohawk Studio, seção 4: o administrador da galeria escolhe por quanto tempo os dados faciais são guardados («Sem prazo» por padrão, ou 30, 90 ou 180 dias ou 12 meses depois do envio); arquivar uma galeria não exclui mais os seus dados faciais; os dados faciais são excluídos 30 dias depois que a busca facial é desativada, quando a galeria ou a foto é excluída, e em até 24 horas depois do fechamento de um espaço de trabalho. As seções 5 e 6 acompanham.",
      "1.2, 28 de outubro de 2026: o app Photohawk: agrupar rostos e compará-los na nuvem é necessário para usar o app (dois consentimentos separados, retirados com “Excluir todos os dados faciais”); os dados faciais na nuvem não são mais excluídos 30 dias depois que um plano termina; o app não é oferecido em Illinois; idades estimadas, aniversários e parentescos ficam no celular. Política completa do app v1.1.",
      "1.1, 22 de outubro de 2026: Photohawk Studio, seção 5 reescrita: qual é a base legal para as fotos e para a busca facial, e o que o administrador decide.",
      "1.0, 20 de outubro de 2026: primeira versão. Uma página com uma política própria para o Photohawk Studio e outra para o Photohawk Family; a política de dados faciais do Photohawk Family deixa de ser um rascunho.",
    ],
  },
  seeAlso: {
    text: "Como os dados faciais são tratados no Photohawk Studio e no app Photohawk:",
    label: "Política de dados faciais",
  },
  familyPage: {
    seoTitle: "Photohawk app: Face Data Policy | Photohawk",
    description:
      "A política de dados faciais completa do app Photohawk: quais dados faciais ele cria, onde ficam guardados e o seu calendário de conservação e destruição.",
    back: "Política de dados faciais dos dois produtos",
    englishOnly: "Este documento está disponível apenas em inglês.",
  },
};

export default pt;
